<template>
  <div class="mgi-notification-bell">
    <v-menu location="bottom end">
      <template #activator="{ props }">
        <v-btn v-bind="props" icon variant="tonal" color="primary" :title="`Notificaciones (${unreadCount})`">
          <v-badge :content="unreadCount" :model-value="unreadCount>0" color="error" offset-x="-2" offset-y="-2" bordered>
            <v-icon>mdi-bell</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <v-list min-width="320" density="comfortable">
        <v-list-subheader class="d-flex align-center justify-space-between">
          <span>Notificaciones recientes</span>
          <v-chip :color="statusColor" size="x-small" variant="tonal">
            {{ statusLabel }}
          </v-chip>
        </v-list-subheader>
        <v-alert
          v-if="degradedMessage"
          :type="syncError ? 'warning' : 'info'"
          variant="tonal"
          density="compact"
          class="ma-2"
        >
          {{ degradedMessage }}
        </v-alert>
        <template v-if="items.length">
          <v-list-item v-for="n in items" :key="n.id" @click="openCenter(n)">
            <template #prepend>
              <v-icon :color="iconColor(n)" size="18">{{ iconFor(n) }}</v-icon>
            </template>
            <v-list-item-title class="text-truncate">{{ n.title }}</v-list-item-title>
            <v-list-item-subtitle class="text-truncate">
              {{ n.message }}
              <span v-if="n.origin === 'cache'" class="text-caption text-medium-emphasis"> · cache</span>
              <span v-else-if="!n.persisted" class="text-caption text-medium-emphasis"> · local</span>
            </v-list-item-subtitle>
            <template #append>
              <v-chip v-if="!n.read" size="x-small" color="primary" label>new</v-chip>
            </template>
          </v-list-item>
          <v-divider class="my-1" />
          <v-list-item @click="openCenter()" title="Abrir centro de notificaciones" prepend-icon="mdi-open-in-new" />
        </template>
        <template v-else>
          <v-list-item title="Sin notificaciones" prepend-icon="mdi-bell-off-outline" />
        </template>
      </v-list>
    </v-menu>

    <v-dialog v-model="centerOpen" max-width="720">
      <NotificationCenter :show-statistics="false" :enable-real-time="true" @notification-select="onSelect" />
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useNotificationsStore } from '@/stores/notifications'
import NotificationCenter from '@/components/NotificationCenter.vue'

const store = useNotificationsStore()
const { unreadCount, last10, syncing, syncError, usingCachedData, realtimeConnected, realtimeOnly } = storeToRefs(store)
const items = last10
const centerOpen = ref(false)

onMounted(() => {
  void store.initialize()
})

function openCenter(n?: any) {
  centerOpen.value = true
  if (n?.id) void store.markAsReadAndSync(n.id)
}

function iconFor(n: any) {
  switch (n.type) {
    case 'success': return 'mdi-check-circle'
    case 'warning': return 'mdi-alert'
    case 'error': return 'mdi-alert-circle'
    case 'system': return 'mdi-bell'
    default: return 'mdi-information'
  }
}

function iconColor(n: any) {
  switch (n.type) {
    case 'success': return 'success'
    case 'warning': return 'warning'
    case 'error': return 'error'
    default: return 'primary'
  }
}

function onSelect(n: any) {
  if (n?.id) void store.markAsReadAndSync(n.id)
}

// WS4 · H2: a delivered identity is not admitted to the durable reader; its bell is the realtime push and
// nothing else, so it never reports a backend sync as pending, cached or degraded (see
// services/notifications.admission.ts)
const statusLabel = computed(() => {
  if (realtimeOnly.value) return realtimeConnected.value ? 'tiempo real' : 'sin realtime'
  if (syncing.value) return 'sincronizando'
  if (syncError.value) return 'degradado'
  if (usingCachedData.value) return 'cache'
  if (!realtimeConnected.value) return 'sin realtime'
  return 'sincronizado'
})

const statusColor = computed(() => {
  if (realtimeOnly.value) return realtimeConnected.value ? 'success' : 'orange'
  if (syncing.value) return 'info'
  if (syncError.value) return 'warning'
  if (usingCachedData.value || !realtimeConnected.value) return 'orange'
  return 'success'
})

const degradedMessage = computed(() => {
  if (realtimeOnly.value) return realtimeConnected.value ? '' : 'Realtime no conectado; las nuevas notificaciones pueden demorarse.'
  if (syncError.value) return `No se pudo sincronizar con backend: ${syncError.value}`
  if (usingCachedData.value) return 'Mostrando datos cacheados hasta recuperar sincronización.'
  if (!realtimeConnected.value) return 'Realtime no conectado; las nuevas notificaciones pueden demorarse.'
  return ''
})
</script>

<style scoped>
.mgi-notification-bell {
  display: inline-flex;
}
</style>
